/* Presentation only: consume the media already selected by product.js. */
(() => {
 class PDPGalleryLayout extends HTMLElement {
  connectedCallback() {
   this.gallery = this.querySelector('[data-pdp-gallery]');
   this.track = this.querySelector('[data-pdp-thumb-track]');
   this.counter = this.querySelector('[data-pdp-gallery-count]');
   if (!this.gallery || !this.track || this.observer) return;
   this.onClick = event => {
    const button = event.target.closest('[data-pdp-thumb]');
    if (button && this.track.contains(button)) this.select(Number(button.dataset.pdpThumb));
   };
   this.onKey = event => {
    const button = event.target.closest('[data-pdp-thumb]');
    if (!button || !this.track.contains(button)) return;
    const index = Number(button.dataset.pdpThumb);
    const next = {ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: this.slides.length - 1}[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(Math.max(0, Math.min(this.slides.length - 1, next)), true);
   };
   this.track.addEventListener('click', this.onClick);
   this.track.addEventListener('keydown', this.onKey);
   this.observer = new MutationObserver(() => this.rebuild());
   this.observer.observe(this.gallery, {childList: true});
   this.controls = document.createElement('div');
   this.controls.className = 'pdp-gallery-arrows';
   this.previous = document.createElement('button');
   this.next = document.createElement('button');
   for (const [button, label, symbol, step] of [[this.previous, 'Previous product image', '‹', -1], [this.next, 'Next product image', '›', 1]]) {
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.textContent = symbol;
    button.addEventListener('click', () => this.step(step));
    this.controls.append(button);
   }
   this.append(this.controls);
   this.onTouchStart = event => {
    this.touchStart = event.touches.length === 1 && !event.target.closest('video,iframe,model-viewer,button,a')
     ? {x:event.touches[0].clientX, y:event.touches[0].clientY} : null;
   };
   this.onTouchEnd = event => {
    const start = this.touchStart;
    this.touchStart = null;
    if (!start || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
   };
   this.onTouchCancel = () => {this.touchStart = null;};
   this.gallery.addEventListener('touchstart', this.onTouchStart, {passive:true});
   this.gallery.addEventListener('touchend', this.onTouchEnd, {passive:true});
   this.gallery.addEventListener('touchcancel', this.onTouchCancel, {passive:true});
   this.rebuild();
  }
  step(direction) {
   if (this.slides?.length > 1) this.select((Number(this.gallery.dataset.activeIndex || 0) + direction + this.slides.length) % this.slides.length);
  }
  disconnectedCallback() {
   this.gallery?.removeEventListener('touchstart', this.onTouchStart);
   this.gallery?.removeEventListener('touchend', this.onTouchEnd);
   this.gallery?.removeEventListener('touchcancel', this.onTouchCancel);
   this.controls?.remove();
   this.videoDialog?.close();
   this.videoDialog?.remove();
   this.videoRail?.remove();
   this.observer?.disconnect();
   this.observer = null;
   this.track?.removeEventListener('click', this.onClick);
   this.track?.removeEventListener('keydown', this.onKey);
  }
  prepareVideo(slide) {
   const video = slide.querySelector('video');
   if (!video || video.dataset.pdpEnhanced) return;
   video.dataset.pdpEnhanced = 'true';
   slide.classList.add('pdp-video-slide');
   const source = slide.dataset.pdpThumbnail || video.getAttribute('poster');
   if (source) {
    const poster = new URL(source, location.href);
    poster.pathname = poster.pathname.replace(/_small(?=\.[^.]+$)/, '');
    poster.searchParams.set('width', '1200');
    video.poster = poster.href;
   }
   video.playsInline = true;
   const play = document.createElement('button');
   play.type = 'button'; play.className = 'pdp-video-play';
   play.setAttribute('aria-label', 'Play product video');
   const icon = document.createElement('span');
   icon.setAttribute('aria-hidden', 'true'); icon.textContent = '▶';
   const label = document.createElement('span'); label.textContent = 'Play video';
   play.append(icon, label);
   const status = document.createElement('div');
   status.className = 'pdp-video-status'; status.setAttribute('role', 'status');
   status.setAttribute('aria-live', 'polite'); status.hidden = true;
   slide.append(play, status);
   const state = (name, message = '') => {
    slide.dataset.videoState = name;
    play.hidden = name === 'playing' || name === 'loading';
    status.hidden = !message; status.textContent = message;
    play.setAttribute('aria-label', name === 'error' ? 'Retry product video' : 'Play product video');
    label.textContent = name === 'error' ? 'Try again' : video.ended ? 'Replay video' : 'Play video';
   };
   const start = () => {
    state('loading', 'Loading video…');
    if (video.error) video.load();
    const request = video.play();
    request?.catch(error => {
     if (error.name === 'AbortError' || slide.hidden || !slide.isConnected) return;
     state('error', 'Video could not start. Please try again.');
    });
   };
   play.addEventListener('click', start);
   video.addEventListener('play', () => state('loading', 'Loading video…'));
   video.addEventListener('playing', () => {
    if (slide.hidden) { video.pause(); return; }
    state('playing');
   });
   video.addEventListener('waiting', () => { if (!video.paused) state('loading', 'Loading video…'); });
   video.addEventListener('pause', () => state('paused'));
   video.addEventListener('ended', () => state('paused'));
   video.addEventListener('error', () => state('error', 'Video could not load. Please try again.'));
   state('paused');
  }
  buildVideoRail(slides, color) {
   this.videoDialog?.close();
   this.videoRail?.remove();
   if (!slides.length || !color) return;
   const rail = document.createElement('div');
   rail.className = 'pdp-video-rail'; rail.setAttribute('role', 'group');
   rail.setAttribute('aria-label', 'Product videos');
   slides.forEach((slide, index) => {
    const video = slide.querySelector('video');
    const source = slide.dataset.pdpThumbnail || video.poster;
    const poster = new URL(source, location.href);
    poster.pathname = poster.pathname.replace(/_small(?=\.[^.]+$)/, '');
    poster.searchParams.set('width', '1200');
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'pdp-video-entry';
    button.setAttribute('aria-label', 'Watch product video ' + (index + 1));
    button.setAttribute('aria-haspopup', 'dialog');
    const image = document.createElement('img');
    image.src = poster.href; image.alt = ''; image.width = 60; image.height = 60;
    const icon = document.createElement('span');
    icon.textContent = '▶'; icon.setAttribute('aria-hidden', 'true');
    button.append(image, icon);
    button.addEventListener('click', () => this.openVideo(video, poster.href, button, index));
    rail.append(button);
   });
   color.before(rail); this.videoRail = rail;
  }
  openVideo(source, poster, opener, index) {
   this.videoDialog?.close(); this.videoDialog?.remove();
   const dialog = document.createElement('dialog');
   dialog.className = 'pdp-video-dialog';
   dialog.setAttribute('aria-label', 'Product video ' + (index + 1));
   const frame = document.createElement('div');
   frame.className = 'pdp-video-frame';
   const video = source.cloneNode(true);
   delete video.dataset.pdpEnhanced;
   video.poster = poster; video.controls = true; video.playsInline = true;
   video.preload = 'auto'; video.autoplay = false; video.loop = false;
   frame.append(video); this.prepareVideo(frame);
   const close = document.createElement('button');
   close.type = 'button'; close.className = 'pdp-video-close';
   close.setAttribute('aria-label', 'Close product video'); close.textContent = '×';
   close.addEventListener('click', () => dialog.close());
   frame.append(close); dialog.append(frame); document.body.append(dialog);
   this.videoDialog = dialog;
   const previousOverflow = document.documentElement.style.overflow;
   dialog.addEventListener('close', () => {
    video.pause();
    document.documentElement.style.overflow = previousOverflow;
    if (opener.isConnected) opener.focus({preventScroll:true});
   }, {once:true});
   dialog.addEventListener('click', event => {if (event.target === dialog) dialog.close();});
   dialog.showModal(); document.documentElement.style.overflow = 'hidden';
   close.focus({preventScroll:true});
   frame.querySelector('.pdp-video-play').click();
  }
  rebuild() {
   const restore = this.track.contains(document.activeElement);
   const media = [...this.gallery.children].filter(node => node.hasAttribute('data-media-id'));
   const color = [...(this.closest('product-view')?.querySelectorAll('[data-option-position]') || [])].find(field => /colou?r/i.test(field.dataset.optionName || ''));
   const videos = color ? media.filter(slide => slide.querySelector('video')) : [];
   this.buildVideoRail(videos, color);
   videos.forEach(slide => {slide.hidden = true; slide.querySelector('video').pause();});
   this.slides = media.filter(slide => !videos.includes(slide));
   this.slides.forEach(slide => this.prepareVideo(slide));
   const buttons = this.slides.map((slide, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.pdpThumb = String(index);
    const kind = slide.dataset.pdpMediaKind || 'image';
    const alt = slide.querySelector('img')?.alt || '';
    button.setAttribute('aria-label', 'View ' + kind.replace('_', ' ') + ' ' + (index + 1) + (alt ? ': ' + alt : ''));
    const src = slide.dataset.pdpThumbnail || slide.querySelector('img')?.getAttribute('src');
    if (src) {
     const image = document.createElement('img');
     image.src = src; image.alt = ''; image.loading = 'lazy';
     image.width = 80; image.height = 120;
     button.append(image);
    } else button.textContent = String(index + 1);
    if (kind !== 'image') {
     const label = document.createElement('span');
     label.textContent = kind === 'model' ? '3D' : 'Video';
     button.append(label);
    }
    return button;
   });
   this.track.replaceChildren(...buttons);
   this.track.hidden = buttons.length < 2;
   if (this.controls) this.controls.hidden = buttons.length < 2;
   this.counter.hidden = !buttons.length;
   this.classList.add('pdp-gallery-ready');
   this.select(0, restore && buttons.length > 1);
  }
  select(index, focus = false) {
   if (!this.slides?.[index]) return;
   this.slides.forEach((slide, current) => {
    if (current !== index && !slide.hidden) {
     slide.querySelectorAll('video').forEach(video => video.pause());
     slide.querySelectorAll('iframe').forEach(frame => {
      const src = frame.getAttribute('src');
      if (src) frame.setAttribute('src', src);
     });
    }
    slide.hidden = current !== index;
   });
   const selectedVideo = this.slides[index].querySelector('video');
   if (selectedVideo && selectedVideo.preload === 'none') selectedVideo.preload = 'metadata';
   const buttons = [...this.track.children];
   buttons.forEach((button, current) => {
    button.setAttribute('aria-pressed', String(current === index));
    button.tabIndex = current === index ? 0 : -1;
   });
   this.gallery.dataset.activeIndex = String(index);
   this.counter.textContent = (index + 1) + ' / ' + this.slides.length;
   const button = buttons[index];
   if (focus) button.focus({preventScroll: true});
   if (button) {
    const left = button.offsetLeft;
    if (left < this.track.scrollLeft) this.track.scrollLeft = left;
    else if (left + button.offsetWidth > this.track.scrollLeft + this.track.clientWidth)
     this.track.scrollLeft = left + button.offsetWidth - this.track.clientWidth;
    const top = button.offsetTop;
    if (top < this.track.scrollTop) this.track.scrollTop = top;
    else if (top + button.offsetHeight > this.track.scrollTop + this.track.clientHeight)
     this.track.scrollTop = top + button.offsetHeight - this.track.clientHeight;
   }
  }
 }
 if (!customElements.get('pdp-gallery-layout')) customElements.define('pdp-gallery-layout', PDPGalleryLayout);
})();


